import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import './box-builder-progress.css'

class BoxBuilderProgress extends HTMLElement {
  #boxState = BoxState.getInstance()
  #stepNames = ['pets', 'sampler', 'summary']
  #stepElements = new Map()

  constructor() {
    super()
  }

  connectedCallback() {
    this.render()
    this.#cacheElements()
    this.#boxState.subscribe(EVENT_TYPES.ACTIVE_VIEW_CHANGE, this.#handleViewChange)
    this.#updateVisibility()
    this.#updateStepStates()
  }

  disconnectedCallback() {
    this.#boxState.unsubscribe(EVENT_TYPES.ACTIVE_VIEW_CHANGE, this.#handleViewChange)
  }

  render() {
    this.innerHTML = `
      <nav class="v:w-full v:relative v:mx-auto v:max-w-[600px] v:h-10 v:transition-opacity v:duration-300 v:ease-in-out" aria-label="Box builder progress" role="navigation">
        ${this.#renderStep('summary', 'Summary', 'v:w-full', 'v:z-10')}
        ${this.#renderStep('sampler', 'Sampler Recipes', 'v:w-1/2', 'v:z-20')}
        ${this.#renderStep('pets', 'My Pets', 'v:w-0', 'v:z-30')}
      </nav>
    `
  }

  #renderStep(name, label, width, zIndex) {
    return `
      <div class="progress-step progress-step--${name} v:absolute v:inset-0 v:h-10 ${width} v:overflow-visible ${zIndex} v:transition-all v:duration-300 v:ease-in-out">
        <div class="progress-step__line-base v:absolute v:left-0 v:right-0 v:bottom-[5px] v:h-0.5 v:bg-gray-25"></div>
        <div class="progress-step__line v:absolute v:left-0 v:bottom-[5px] v:h-0.5 v:bg-brand-orange-dark"></div>
        <div class="progress-step__indicator v:absolute v:right-0 v:bottom-0 v:w-3 v:h-3 v:rounded-full v:translate-x-1/2 v:border-2">
          <span class="progress-step__label v:absolute v:bottom-full v:left-1/2 v:-translate-x-1/2 v:mb-2 v:label-md v:whitespace-nowrap">${label}</span>
        </div>
      </div>
    `
  }

  #cacheElements() {
    this.#stepNames.forEach(name => {
      const step = this.querySelector(`.progress-step--${name}`)
      const label = step?.querySelector('.progress-step__label')
      this.#stepElements.set(name, { step, label })
    })
  }

  #handleViewChange = (newView) => {
    this.#updateVisibility(newView)
    this.#updateStepStates(newView)
  }

  #updateVisibility(view) {
    if (!this.#boxState) return

    const currentView = view || this.#boxState.activeView
    const activeIndex = this.#stepNames.indexOf(currentView)

    this.hidden = activeIndex < 1
  }

  #updateStepStates(view) {
    if (!this.#boxState) return

    const currentView = view || this.#boxState.activeView
    const activeIndex = this.#stepNames.indexOf(currentView)

    this.#stepNames.forEach((stepName, index) => {
      let state
      if (index < activeIndex) {
        state = 'completed'
      } else if (index === activeIndex) {
        state = 'active'
      } else {
        state = 'disabled'
      }
      this.#setStepState(stepName, state)
    })
  }

  #setStepState(stepName, state) {
    const elements = this.#stepElements.get(stepName)
    if (!elements?.step || !elements?.label) return

    const { step, label } = elements
    const labelText = label.textContent

    step.setAttribute('data-state', state)

    if (state === 'completed') {
      step.removeAttribute('aria-current')
      step.setAttribute('aria-label', `${labelText}: Completed`)
    } else if (state === 'active') {
      step.setAttribute('aria-current', 'step')
      step.setAttribute('aria-label', `${labelText}: Current step`)
    } else {
      step.removeAttribute('aria-current')
      step.setAttribute('aria-label', `${labelText}: Not started`)
    }
  }
}

if (!customElements.get('box-builder-progress')) {
  customElements.define('box-builder-progress', BoxBuilderProgress)
}
